import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

const HIDDEN = 'store-header-group--hidden';
const STICKY = 'store-header-group--sticky';
const ANIMATE = 'store-header-group--animate';

/**
 * Hides the header after it scrolls off, then slides it back when the page scrolls up.
 * Matches the live storefront: sticky only after the header has left the viewport.
 */
class StoreHeaderSticky {
  /**
   * @param {HTMLElement} headerGroup
   */
  constructor(headerGroup) {
    this.headerGroup = headerGroup;
    this.headerBounds = { top: 0, bottom: 0 };
    this.currentScrollTop = 0;
    /** @type {EventTarget | null} */
    this.scrollTarget = null;
    /** @type {number | null} */
    this.rafId = null;

    this.onScroll = this.onScroll.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  start() {
    this.syncAnnouncementOffset();
    this.measure();
    this.bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.onBreakpointChange);
    window.addEventListener('resize', this.onResize);
  }

  syncAnnouncementOffset() {
    const announcement = this.headerGroup.querySelector('.store-header__announcement');
    const height = announcement ? Math.ceil(announcement.getBoundingClientRect().height) : 0;
    this.headerGroup.style.setProperty('--store-header-announcement-height', `${height}px`);
  }

  measure() {
    if (this.headerGroup.classList.contains(STICKY)) return;

    const scrollTop = getScrollTop();
    const rect = this.headerGroup.getBoundingClientRect();
    this.headerBounds = {
      top: Math.max(0, scrollTop + rect.top),
      bottom: scrollTop + rect.bottom,
    };
  }

  bindScroll() {
    this.scrollTarget?.removeEventListener('scroll', this.onScroll);
    this.scrollTarget = getScrollEventTarget();
    this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true });
    this.currentScrollTop = getScrollTop();
  }

  onBreakpointChange() {
    this.bindScroll();
    this.measure();
  }

  onResize() {
    this.syncAnnouncementOffset();
    this.measure();
  }

  onScroll() {
    if (this.rafId !== null) return;

    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  }

  isOverlayOpen() {
    return Boolean(this.headerGroup.querySelector(':popover-open'));
  }

  update() {
    const scrollTop = getScrollTop();
    const scrollingDown = scrollTop > this.currentScrollTop;
    const scrollingUp = scrollTop < this.currentScrollTop;

    if (scrollingDown && scrollTop > this.headerBounds.bottom) {
      if (!this.isOverlayOpen()) this.hide();
    } else if (scrollingUp && scrollTop > this.headerBounds.bottom) {
      this.reveal();
    } else if (scrollTop <= this.headerBounds.top) {
      this.reset();
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    this.syncAnnouncementOffset();
    this.headerGroup.classList.add(HIDDEN, STICKY);
  }

  reveal() {
    this.headerGroup.classList.add(STICKY, ANIMATE);
    this.headerGroup.classList.remove(HIDDEN);
  }

  reset() {
    this.headerGroup.classList.remove(HIDDEN, STICKY, ANIMATE);
  }
}

function initStoreHeaderSticky() {
  const headerGroup = document.getElementById('header-group');
  if (!headerGroup?.querySelector('.store-header')) return;
  if (headerGroup.dataset.storeHeaderSticky === 'ready') return;

  headerGroup.dataset.storeHeaderSticky = 'ready';
  new StoreHeaderSticky(headerGroup).start();
}

initStoreHeaderSticky();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.querySelector('.store-header')) return;
  initStoreHeaderSticky();
});
